import React, { useEffect, useState } from 'react';
import {
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Pencil, Trash, X } from 'react-bootstrap-icons';
import docsgql from 'gql/dashboard/sales/salesOrders.gql';
import { useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosClient from 'utils/axios';
import useAlert from 'hooks/useAlert';
import { toast } from 'react-toastify';
import DefaultCurrency from 'components/DefaultCurrency';
import DateFormat from 'components/DateFormat';
import Details from '../details/Details';
import InfiniteScroll from 'react-infinite-scroll-component';
import ScrollToTop from 'components/ScrollToTop';
import * as Sentry from "@sentry/react";

const SalesOrders = () => {
  // states
  const [docs, setDocs] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchTimer, setSearchTimer] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [pageInfo, setPageInfo] = useState({
    totalPages: 0,
    totalRecords: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // hooks
  const { loading, data, error, refetch } = useQuery(docsgql, {
    variables: { orderBy: '-created_at', page: 1, search: '' },
    notifyOnNetworkStatusChange: true,
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const alert = useAlert();

  // functions
  const search = (val) => {
    if (!searchValue && !val) return;

    setSearchValue(val);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(
      setTimeout(() => {
        setDocs([]);
        setCurrentPage(1);
        refetch({
          search: val,
          page: 1,
        });
      }, 400)
    );
  };

  const next = () => {
    if (loading) return;

    if (currentPage < pageInfo.totalPages) {
      refetch({
        page: currentPage + 1,
      });
      setCurrentPage((currentPage) => currentPage + 1);
    }
  };

  const remove = (doc) => {
    alert
      .fire({
        title: `Remove ${doc.docNumber}`,
        text: 'Do you want to remove this document?',
        icon: 'warning',
        showDenyButton: true,
        confirmButtonText: 'Cancel',
        denyButtonText: 'Yes, delete it!',
      })
      .then((confirmed) => {
        if (confirmed.isDenied) {
          toast.info('Deleting...');
          axiosClient
            .delete(`/sales/salesorders/${doc.id}/`)
            .then((data) => {
              setDocs((docs) => docs.filter((x) => x.id !== doc.id));
              toast.success('Document is deleted.');
              setSelectedOrder(null);
            })
            .catch((err) => {
              Sentry.captureException(err);
              toast.error(err.response.data.detail.error);
            });
        }
      });
  };

  // effects
  useEffect(() => {
    if (!loading && data) {
      setDocs((docs) => [...docs, ...data.salesOrders]);
      if (data.salesOrders?.length) {
        setPageInfo(data.salesOrders[0].pageInfo);
      }
    }
  }, [loading]);

  useEffect(() => {
    if (docs.length && searchParams.get('open')) {
      setSelectedOrder(docs.find((x) => x.id == searchParams.get('open')));
      setSearchParams({});
    }
  }, [docs]);

  useEffect(() => {
    if (currentPage && pageInfo && currentPage === pageInfo?.totalPages) {
      setHasMore(false);
    }
  }, [currentPage, pageInfo]);

  return (
    <React.Fragment>
      <ScrollToTop />
      {selectedOrder && (
        <Details
          order={selectedOrder}
          setOrder={setSelectedOrder}
          edit={(x) => navigate(`/dashboard/sales/orders/form/${x.id}`)}
          remove={remove}
        />
      )}
      <InfiniteScroll
        dataLength={docs?.length || 0}
        className='overflow-hidden'
        next={next}
        hasMore={hasMore}
        loader={
          <Row className='justify-content-center'>
            {loading && <Spinner className='w-auto' />}
          </Row>
        }
        endMessage={<p className='text-muted text-center'>No more data...</p>}
      >
        <Container fluid className='mt-2'>
          <Row>
            <InputGroup className='mb-3'>
              <Form.Control
                value={searchValue}
                onChange={(e) => search(e.target.value)}
                placeholder='Search...'
                className='w-auto'
              />
              <X
                className='w-auto h-100 border rounded-right'
                onClick={() => search('')}
                size={20}
              />
            </InputGroup>
          </Row>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Doc Number</th>
                <th>Customer</th>
                <th>Total Sales Value</th>
                <th>Doc Date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((x, index) => (
                <tr key={x.id}>
                  <td>{index + 1}</td>
                  <td>
                    <span
                      className='link-primary cursor-pointer'
                      onClick={() => setSelectedOrder(x)}
                    >
                      {x.docNumber}
                    </span>
                  </td>
                  <td>{x.customer.name}</td>
                  <td>
                    <DefaultCurrency value={x.totalSalesValue} />
                  </td>
                  <td>
                    <DateFormat value={x.docDate} />
                  </td>
                  <td>{x.status}</td>
                  <td className='text-center'>
                    <Pencil
                      size={20}
                      color='darkorange'
                      className='mx-2 cursor-pointer'
                      onClick={() =>
                        navigate(`/dashboard/sales/orders/form/${x.id}`)
                      }
                    />
                    <Trash
                      onClick={() => remove(x)}
                      size={20}
                      color='darkred'
                      className='mx-2 cursor-pointer'
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Container>
      </InfiniteScroll>
    </React.Fragment>
  );
};

export default SalesOrders;
